import { useEffect } from 'react';
import { isNative } from '@/lib/capacitor';
import { layoutViewportHeight } from '@/lib/viewport';

/**
 * Prevents sticky/absolute footer elements from floating above the software keyboard
 * on native mobile apps (Capacitor Android & iOS).
 *
 * How it works:
 *  - Android (WebView resize mode): `window.innerHeight` shrinks when the keyboard opens.
 *    We track the maximum `innerHeight` ever seen as the "true" full-screen height and
 *    compute keyboard height = maxHeight - visualViewport.height.
 *  - iOS (visual viewport shrinks): `window.innerHeight` stays constant but
 *    `visualViewport.height` decreases by the keyboard height, so the same formula works.
 *
 * The result is written to the CSS custom property `--keyboard-height` on <html>.
 * Footer elements that carry the `keyboard-aware-footer` Tailwind/CSS class consume
 * this property to translateY themselves below the keyboard boundary, effectively
 * hiding them under the keyboard while the user is typing.
 *
 * This hook is a no-op in web browsers (non-native) where the keyboard does not
 * cause the described overlap issue.
 */
export function useNativeKeyboardFix(): void {
    useEffect(() => {
        if (!isNative()) return;

        const viewport = window.visualViewport;
        if (!viewport) return;

        // Seed with the current full-screen height. We update this on every resize
        // so that if the app rotates or the screen size changes legitimately we adapt.
        let maxHeight = window.innerHeight;
        // Zoom-corrected throughout: a pinch shrinks viewport.height exactly like a keyboard.
        let lastViewportHeight = layoutViewportHeight(viewport);

        const isEditableElement = (element: Element | null): boolean => {
            if (!(element instanceof HTMLElement)) return false;
            return !!element.closest('input, textarea, [contenteditable="true"], .ProseMirror');
        };

        const update = () => {
            // Keep maxHeight as the largest innerHeight we have ever observed.
            // On Android this is the pre-keyboard height; on iOS innerHeight never shrinks.
            maxHeight = Math.max(maxHeight, window.innerHeight);

            // Keyboard height is how much shorter the visual viewport is vs. the full height.
            let keyboardHeight = Math.max(0, maxHeight - layoutViewportHeight(viewport));
            if (!isEditableElement(document.activeElement)) {
                keyboardHeight = 0;
            }
            const isKeyboardOpen = keyboardHeight > 120;

            document.documentElement.style.setProperty('--keyboard-height', `${keyboardHeight}px`);
            // Also expose the pre-keyboard full height so CSS can compute
            // constrained max-heights without relying on dvh units (unsupported
            // in older Android WebViews).
            document.documentElement.style.setProperty('--full-viewport-height', `${maxHeight}px`);
            document.documentElement.dataset.keyboardOpen = isKeyboardOpen ? 'true' : 'false';
            lastViewportHeight = layoutViewportHeight(viewport);
        };

        const resetMaxHeight = () => {
            maxHeight = window.innerHeight;
            requestAnimationFrame(update);
        };

        const handleWindowResize = () => {
            const viewportGrowth = layoutViewportHeight(viewport) - lastViewportHeight;
            // Re-baseline only once the keyboard is gone. With a field still focused the growth is
            // the keyboard changing size (text → number pad is ~100px shorter on Android), and
            // re-baselining then would measure the "full" height with the keyboard still up.
            if (viewportGrowth > 80 && !isEditableElement(document.activeElement)) {
                resetMaxHeight();
                return;
            }
            update();
        };

        viewport.addEventListener('resize', update);
        window.addEventListener('resize', handleWindowResize);
        window.addEventListener('orientationchange', resetMaxHeight);
        const screenOrientation = window.screen.orientation;
        screenOrientation?.addEventListener('change', resetMaxHeight);
        update();

        return () => {
            viewport.removeEventListener('resize', update);
            window.removeEventListener('resize', handleWindowResize);
            window.removeEventListener('orientationchange', resetMaxHeight);
            screenOrientation?.removeEventListener('change', resetMaxHeight);
            document.documentElement.style.removeProperty('--keyboard-height');
            document.documentElement.style.removeProperty('--full-viewport-height');
            delete document.documentElement.dataset.keyboardOpen;
        };
    }, []);
}
